import {
  CaseId,
  FolderId,
  TDOId,
  TreeObjectId,
} from '@common/state/modules/universal/models/Brands';
import {
  AssetFileType,
  FileFilters,
  FolderStateType,
  LegacyMediaDetailType,
  LegacyMediaType,
  SoftDeletedMediaFileDataType,
  VirtualFolderNames,
} from '@redact-modules/casemedia/store';
import { FolderPathItem } from '@redact-modules/helper';
import { REDACTION_STATUS_LABELS } from '@common-modules/mediaDetails/redactionStatuses';
import { JobStatus } from '@veritone/glc-redux';
import type { StatusHistoryNoteOutcome } from '@common-modules/statusHistory/models';

export type Action<P, M = undefined> = M extends undefined
  ? {
      type: string;
      payload: P;
    }
  : {
      type: string;
      payload: P;
      meta: M;
    };
export interface FetchRootFolderRequest {
  limit: number;
  offset: number;
}

export interface FetchSubfolderRequest {
  isCase: boolean;
  folderId: FolderId;
  limit: number;
  offset: number;
}

export interface MoveFolderRequest {
  treeObjectId: TreeObjectId;
  oldParentTreeId: FolderId;
  newParentTreeId: FolderId;
  // VE-21114: v2 authorizes moveFolder via OLP against folder ids (the live
  // folderId/fromFolderId/toFolderId inputs); the deprecated treeObject ids
  // return not_allowed. treeObjectId above is kept for state correlation.
  folderId?: FolderId;
  oldParentId?: FolderId;
  newParentId?: FolderId;
  isRoot: boolean;
  suppressSnackbar?: boolean;
}

export interface DownloadAllJobStatusRequest {
  jobId: string;
  exportTdoId: TDOId;
  treeObjectId: TreeObjectId;
}

export interface ExportFilesRequest {
  treeObjectId: TreeObjectId;
  exportTdoId: TDOId;
}

export interface ExportTdoRequest {
  exportTdoId: TDOId;
  treeObjectId: TreeObjectId | FolderId | CaseId;
}

export interface CreateNewFolderPayload {
  newFolderName: string;
  parentId: FolderId;
  isRoot: boolean;
  // Type of the item being CREATED (picks the create mutation/schema link).
  isCase: boolean;
  // Type of the PARENT container, computed by the Add dialogs (which hold
  // the parent object). The post-create refetch keys its query on this —
  // it must never be inferred from the child's type (VE-25228 D5).
  parentIsCase: boolean;
}

export interface ActionSetCaseMedia {
  jobId?: string;
  tdoId: string;
  status: JobStatus;
}

export interface ActionFetchFolderRequest {
  limit?: number;
  offset?: number;
}

export interface ActionFetchMoreSubFoldersRequest {
  folderId: FolderId;
  limit: number;
  offset: number;
}
export interface ActionFetchMoreFilesRequest {
  caseId: CaseId;
  limit: number;
  offset: number;
}

export interface ActionSelectedFolderSuccessPayload {
  folderId: FolderId | TreeObjectId;
  limit: number;
  offset: number;
  isCase: boolean;
  /**
   * VE-28429: may this reuse a recently-loaded file list instead of refetching?
   *
   * Relayed from `ActionSetSelectedFolderPayload` by `setSelectedFolder`, and set
   * at the origin — the editor's exit re-select — never here. Every other
   * dispatcher of this action fires BECAUSE something changed (upload, new
   * folder, move, delete) or because the user asked (Refresh), so all of them
   * must reach the server. Defaulting to "always fetch" means a new dispatcher
   * gets that for free without knowing the cache exists.
   */
  reuseFreshContents?: boolean;
}

export interface ActionSoftDeleteCaseSdoPayload {
  sdoId: string;
  treeObjectId: TreeObjectId;
  // VE-21114: the case folder's own id, used for the v2 OLP move-to-Trash.
  folderId?: FolderId;
  data: {
    caseName: string;
    status: string;
    folderTreeObjectId: string;
    createdDateTime: string;
    modifiedDateTime: string;
    // Stamped at creation by createNewFolderSdoQuery; carried through the
    // soft-delete spread. Nothing in redact-app reads either.
    priority?: number;
    processingTime?: number;
    isCase?: boolean;
    oldFolderPath?: FolderPathItem[];
    trashRetentionDays?: number;
    /** Legacy stamp on items trashed before the trashRetentionDays rename. */
    numberOfDaysToKeep?: number;
    movedToTrashDateTime?: string;
    movedToTrashByName?: string;
    movedToTrashById?: string;
  };
}

export interface ActionDeleteCaseSdoPayload extends ActionDeleteFolderPayload {
  sdoId: string;
}

export interface ActionDeleteFolderPayload {
  treeObjectId: TreeObjectId;
  // VE-21114: the folder's own id. v2 authorizes deleteFolder via OLP against
  // the folder id, not the deprecated treeObjectId (which returns not_allowed).
  folderId?: FolderId;
  parentId: FolderId;
  folderName: string;
  limit: number;
  isPermanentDelete?: boolean;
  suppressSnackbar?: boolean;
  isCase?: boolean;
}

export interface ActionFetchFolderByIdPayload {
  folderId: FolderId;
}

export interface ActionSetSelectedVirtualFolderPayload {
  virtualFolderName: VirtualFolderNames;
  folder: FolderStateType<CaseId>;
}

export interface ActionSetSelectedFolderPayload {
  folder: FolderStateType;
  isCase: boolean;
  /**
   * VE-28429: may this re-select reuse a recently-loaded file list instead of
   * refetching it?
   *
   * Set by the media editor's exit re-select and by nothing else. Thirteen other
   * call sites dispatch this action — the folder tree, search, Unassigned Media,
   * the toolbar Refresh button, and the post-ingestion refresh in uploads — and
   * every one of them means "go and look again", so they must leave it unset.
   * Deciding this inside `setSelectedFolder` instead looked equivalent and was
   * not: it is one saga serving all thirteen, so it silently made Refresh and
   * the post-upload refresh no-ops inside the window.
   */
  reuseFreshContents?: boolean;
}

export interface ActionDeleteFilePayload {
  tdoId: TDOId;
  fileName: string;
  type: VirtualFolderNames;
  isSoftDelete?: boolean;
  isLegacyTdo?: boolean;
  // VE-25098: set by the batch-trash saga so the per-file success/failure
  // snackbars are suppressed in favor of a single summary snackbar.
  suppressSnackbar?: boolean;
}

export interface ActionCreateExportTdoPayload {
  treeObjectId: TreeObjectId;
}

export interface ActionExportFilePayload {
  treeObjectId: TreeObjectId;
  exportTdoId: TDOId;
}

export interface ActionDownloadAllJobStatusPayload {
  exportTdoId: TDOId;
  jobId: string;
  treeObjectId: TreeObjectId;
}

export interface ActionFetchExportTdoPayload {
  exportTdoId: TDOId;
  treeObjectId: TreeObjectId | FolderId | CaseId;
}

export interface ActionDownloadAllStatusPayload {
  status: boolean;
  treeObjectId: TreeObjectId;
}

export interface ActionDownloadAllForTdoPayload {
  status: boolean;
  treeObjectId: TreeObjectId;
  tdoId: TDOId;
}

export interface ActionCreateNewFolderPayload {
  newFolderName: string;
  parentId: FolderId;
  isCase: boolean;
  isRoot: boolean;
  // The PARENT container's type — see CreateNewFolderPayload.parentIsCase.
  parentIsCase: boolean;
}

export interface ActionMoveFolderPayload {
  treeObjectId: TreeObjectId;
  oldParentTreeId: TreeObjectId | FolderId;
  newParentTreeId: TreeObjectId | FolderId;
  // VE-21114: v2 folder-id equivalents used for the OLP-gated moveFolder inputs
  // (folderId/fromFolderId/toFolderId). See MoveFolderRequest.
  folderId?: FolderId;
  oldParentId?: FolderId;
  newParentId?: FolderId;
  isRoot: boolean;
  suppressSnackbar?: boolean;
}

export interface ActionRemoveSelectedFolderPayload {
  layer: number;
}

export interface ActionNavToCaseMediaPayload {
  folderId: FolderId;
  isCase: boolean;
}

export interface ActionSetShowLegacyMediasPayload {
  isShowLegacyMedias: boolean;
}

export interface ActionFetchLegacyMediasRequest {
  limit: number;
  offset: number;
}

export interface ActionLegacyMediasSuccessPayload {
  structuredDataObjects: {
    records: LegacyMediaType[];
  };
}

export interface ActionFileTdoWithCasePayload {
  newFolderId: FolderId | CaseId | TreeObjectId;
  oldFolderId?: FolderId;
  tdoId: TDOId;
  tdoName: string;
  sdoId: string;
}

export interface ActionDeleteMediaFileSdoPayload {
  sdoId: string;
  tdoId: TDOId;
  tdoName: string;
  isMoveDeleteRequest?: boolean;
  isSoftDelete?: boolean;
  data?: SoftDeletedMediaFileDataType;
}

export interface ActionFetchTdoDetailForSdosPayload {
  tdoIds: TDOId[];
}
export interface ActionRenameFolderPayload {
  id: FolderId;
  name: string;
}
export interface ActionRestoredFolderPayload {
  id: TreeObjectId;
  // VE-21114: the folder's own id. The restore rename (updateFolder) authorizes
  // via OLP against the folder id; the deprecated treeObjectId (`id` above, used
  // for the move) returns not_allowed under v2.
  folderId?: FolderId;
  name: string;
  parentId: FolderId;
}
export interface ActionSetSourceFolderToMovePayload {
  sourceFolderToMove: FolderStateType;
  moveFromFolder: FolderStateType | null | undefined;
}

export interface ActionFetchForExportedFolderPayload {
  folderId: FolderId;
  limit: number;
  offset: number;
}

export interface ActionSetSourceFileToMovePayload {
  sourceFileToMove: LegacyMediaDetailType;
  moveFromFolder: FolderStateType | null | undefined;
}

export enum SearchNameActions {
  ADD = 'Add',
  MOVE = 'Move',
  RENAME = 'Rename',
  RESTORE = 'Restore',
}
export interface SearchNameExistsInFolderRequestPayload {
  folderId: FolderId;
  name: string;
  actionName: SearchNameActions;
  actionPayload:
    | ActionCreateNewFolderPayload
    | ActionMoveFolderPayload
    | ActionRenameFolderPayload
    | ActionRestoredFolderPayload;
  limit?: number;
  offset?: number;
}

export interface ActionFetchSubFoldersPayload {
  isCase: boolean;
  folderId: FolderId | CaseId;
  limit: number;
  offset: number;
  /** VE-28429 — see ActionSelectedFolderSuccessPayload.reuseFreshContents. */
  reuseFreshContents?: boolean;
}

export const RedactionStatuses = REDACTION_STATUS_LABELS;
/** Built-in label or customer-defined status label stored on the TDO tag. */
export type RedactionStatus = string;
export const isRedactionStatus = (value: unknown): value is RedactionStatus =>
  typeof value === 'string' && value.trim().length > 0;

export interface ActionUpdateRedactionStatusChangePayload {
  tdoId: TDOId;
  /** Canonical identity (`redactionStatusKey ?? redactionStatus`). */
  redactionStatus: RedactionStatus;
  /** Human-readable name persisted as the tag's `redactionStatus` (VE-24546). */
  redactionStatusLabel?: string;
  /** Stable UUID persisted as `redactionStatusKey`; custom statuses only. */
  redactionStatusKey?: string;
}

export interface ActionUpdateRedactionStatusRequestPayload {
  tdoId: TDOId;
  /** Canonical identity (`redactionStatusKey ?? redactionStatus`). */
  redactionStatus: RedactionStatus;
  /** Human-readable name persisted as the tag's `redactionStatus` (VE-24546). */
  redactionStatusLabel?: string;
  /** Stable UUID persisted as `redactionStatusKey`; custom statuses only. */
  redactionStatusKey?: string;
  // VE-25098: set by the batch-status saga so the per-file success/failure
  // snackbars are suppressed in favor of a single summary snackbar.
  suppressSnackbar?: boolean;
  /**
   * Correction note for the status history (VE-26730). Travels through redux
   * ONLY — performUpdateRedactionStatus reads it off this payload and puts it on
   * RECORD_STATUS_CHANGE once the mutation has succeeded. Deliberately kept out
   * of the updateTDO mutation's variables (it is user-authored case text the
   * mutation never declares or consumes).
   */
  note?: string;
  /**
   * Optional reply to the note the file was carrying, captured when this
   * transition moves it OFF Correction Needed (VE-26730). Same redux-only
   * transport as `note`, and never a mutation variable for the same reason.
   */
  response?: string;
  responseType?: StatusHistoryNoteOutcome;
}

// VE-25098: bulk redaction-status update for multi-selected input files.
export interface ActionBatchUpdateStatusPayload {
  tdoIds: TDOId[];
  /** Canonical identity (`redactionStatusKey ?? redactionStatus`). */
  redactionStatus: RedactionStatus;
  /** Human-readable name persisted as the tag's `redactionStatus` (VE-24546). */
  redactionStatusLabel?: string;
  /** Stable UUID persisted as `redactionStatusKey`; custom statuses only. */
  redactionStatusKey?: string;
  /** One correction note applied to every selected file's history row (VE-26730). */
  note?: string;
}

// VE-24693 (AC#10): batch-apply watermark snapshots to selected documents.
export interface ActionBatchApplyWatermarkPayload {
  tdoIds: TDOId[];
  watermarks: import('@redact-modules/documents/models').AppliedWatermark[];
}

export interface ActionMoveFileToCasePayload {
  tdoId: TDOId;
  tdoName: string;
  newFolderId: CaseId | TreeObjectId;
  oldFolderId: FolderId | CaseId;
}

export interface ActionSetMoveInputFilePayload {
  sourceInputFileToMove: AssetFileType;
  moveFromFolder: FolderStateType;
}

// VE-25098: batch operations on multi-selected folder-view input files.
export interface BatchFileRef {
  tdoId: TDOId;
  fileName: string;
}

export interface ActionBatchTrashFilesPayload {
  files: BatchFileRef[];
  isSoftDelete: boolean;
}

export interface ActionBatchMoveFilesPayload {
  files: BatchFileRef[];
  newFolderId: CaseId | TreeObjectId;
  oldFolderId: FolderId | CaseId;
}

export interface ActionChangeFileFiltersPayload {
  fileRedacted?: 'redacted' | 'not_redacted' | null;
  /** `'all'`, well-known filter key, or custom status label (VE-24546). */
  status?: string;
  sort?: FileFilters['sort'];
}

export interface ActionSetLastActiveFolderViewFilePayload {
  caseId: CaseId;
  tdoId: TDOId;
  isReturnScrollPending: boolean;
}

export interface ActionMarkFolderViewTdoOpenedPayload {
  tdoId: TDOId;
}

export interface ActionClearLastOpenedFolderViewFileForCasePayload {
  caseId: CaseId;
  tdoId?: TDOId;
}

export interface ExportMediaListFields {
  rootFolder: boolean;
  folderLocation: boolean;
  fileName: boolean;
  fileId: boolean;
  duration: boolean;
  createdDate: boolean;
  modifiedDate: boolean;
  fileStatus: boolean;
  redactionName: boolean;
  hasAudioRedactions: boolean;
  hasVideoRedactions: boolean;
}

export interface ActionExportMediaListPayload {
  selectedFolder?: FolderStateType;
  isRoot?: boolean;
  rootFolder?: FolderStateType;
  fields: ExportMediaListFields;
  isMainPageView: boolean;
}
